/**
 * Editor for a single problem_type within a squad form.
 * Provides:
 *  - identifiers (regex extraction patterns)
 *  - flow_steps (ordered sequence) with one of 18 step_types matching the
 *    backend LogCollector dispatcher (core/log_collector.py).
 *
 * Each step_type declares its own fields. The picker is grouped by category
 * (Databases / Logs / Search) and the form renders the right inputs.
 */
import { useState } from "react";
import { Plus, X, Database, FileText, Search, ChevronUp, ChevronDown, Layers, Activity, Sparkles, Wand2 } from "lucide-react";
import { api } from "../lib/api";

// ─────────────────────────────────────────────────────────────────────────────
// Field type helpers
// ─────────────────────────────────────────────────────────────────────────────
const TEXT = (name, placeholder, hint) => ({ name, placeholder, hint });
const AREA = (name, placeholder, hint) => ({ name, placeholder, hint, textarea: true });
const LIST = (name, placeholder, hint) => ({ name, placeholder, hint, list: true });   // comma-separated
const SELECT = (name, options, hint) => ({ name, options, hint });                       // dropdown

// ─────────────────────────────────────────────────────────────────────────────
// Step type catalog — keys MUST match LogCollector._execute_step dispatch
// ─────────────────────────────────────────────────────────────────────────────
const STEP_TYPES = [
  // ─── Databases ───────────────────────────────────────────────────────────
  { key: "dynamodb", category: "db", label: "DynamoDB query", icon: Database, fields: [
    TEXT("table",      "OrdersTable",                     "Required · table name"),
    TEXT("index",      "tenantId-locator-index",          "Optional · GSI name"),
    TEXT("pk",         "tenantId",                        "PK attribute name"),
    TEXT("pk_from",    "tenant_id",                       "Identifier key to bind to PK"),
    TEXT("sk",         "locator",                         "Optional · SK attribute"),
    TEXT("sk_from",    "locator",                         "Optional · identifier key for SK"),
    SELECT("sk_condition", ["eq", "begins_with"],         "SK comparison (when SK is used)"),
    TEXT("limit",      "25",                              "Max items (default 25)"),
  ]},
  { key: "postgres", category: "db", label: "PostgreSQL query", icon: Database, fields: [
    AREA("query_template", "SELECT * FROM payments WHERE order_id = %(order_id)s", "Use %(identifier_key)s placeholders"),
    TEXT("limit",      "25", "Max rows"),
  ]},
  { key: "mysql", category: "db", label: "MySQL query", icon: Database, fields: [
    AREA("query_template", "SELECT * FROM orders WHERE id = %(order_id)s", "Use %(identifier_key)s placeholders"),
    TEXT("limit",      "25", "Max rows"),
  ]},
  { key: "mssql", category: "db", label: "MS SQL query", icon: Database, fields: [
    AREA("query_template", "SELECT * FROM dbo.Orders WHERE Id = %(order_id)s", "Use %(identifier_key)s placeholders"),
    TEXT("limit",      "25", "Max rows"),
  ]},
  { key: "mongodb", category: "db", label: "MongoDB query", icon: Database, fields: [
    TEXT("collection", "orders",                          "Required · collection name"),
    AREA("filter",     '{"orderId": "{order_id}"}',       "JSON filter · use {identifier_key} placeholders"),
    TEXT("limit",      "50", "Max docs"),
  ]},
  { key: "redis", category: "db", label: "Redis lookup", icon: Database, fields: [
    SELECT("command",  ["get", "hgetall", "keys", "lrange", "smembers", "zrange"], "Command (read-only)"),
    TEXT("key",        "session:{user_id}",               "Key · supports {identifier_key} placeholders"),
    TEXT("start",      "0",                               "lrange/zrange start"),
    TEXT("end",        "99",                              "lrange/zrange end"),
  ]},

  // ─── Logs ────────────────────────────────────────────────────────────────
  { key: "cloudwatch", category: "logs", label: "CloudWatch Insights (search)", icon: Search, fields: [
    LIST("log_groups",     "/aws/lambda/scheduleJob,/aws/lambda/receiveOrder", "Comma-separated log group names"),
    AREA("query_template", "fields @timestamp, @message | filter @message like '{tenant_id}' | limit 50",
                           "Insights query · {identifier} placeholders"),
    LIST("windows_days",   "1,7,30,90",                   "Progressive search windows (days)"),
    TEXT("limit",          "50",                          "Max rows per window"),
  ]},
  { key: "cloudwatch_full", category: "logs", label: "CloudWatch full invocation (end-to-end)", icon: Activity, fields: [
    LIST("log_groups",     "/aws/lambda/processOrder",     "Comma-separated log groups"),
    AREA("discovery_query", "fields @timestamp, @requestId, @logStream | filter @message like '{order_id}' | sort @timestamp desc | limit 50",
                            "Insights query to find request_id + log_stream"),
    TEXT("request_id_from", "request_id",                 "Skip discovery: identifier holding RequestId"),
    TEXT("log_stream_from", "log_stream",                 "Skip discovery: identifier holding log stream"),
    LIST("windows_days",   "1,7,30,90",                   "Discovery search windows (days)"),
    TEXT("max_invocations", "3",                          "Max invocations to fetch full trace for"),
  ]},
  { key: "datadog", category: "logs", label: "Datadog logs", icon: FileText, fields: [
    AREA("query_template", "service:payments {tenant_id}", "Datadog query · {identifier} placeholders"),
    LIST("windows_minutes", "15,60,360,1440",             "Progressive windows (minutes)"),
    TEXT("limit",          "50", "Max rows per window"),
  ]},
  { key: "newrelic", category: "logs", label: "New Relic (NRQL)", icon: FileText, fields: [
    AREA("query_template", "SELECT * FROM Log WHERE message LIKE '%{tenant_id}%' SINCE 1 day ago", "NRQL query"),
  ]},
  { key: "splunk", category: "logs", label: "Splunk (SPL)", icon: FileText, fields: [
    AREA("query_template", "index=app sourcetype=app_log {tenant_id}", "SPL · 'search' prefix added automatically"),
    TEXT("limit",          "50", "Max events"),
  ]},
  { key: "grafana_loki", category: "logs", label: "Grafana Loki (LogQL)", icon: FileText, fields: [
    AREA("query_template", '{app="payments"} |= "{tenant_id}"', "LogQL · {identifier} placeholders"),
    LIST("windows_minutes", "15,60,360,1440",             "Progressive windows (minutes)"),
    TEXT("limit",          "100", "Max lines"),
  ]},
  { key: "gcp_logging", category: "logs", label: "GCP Cloud Logging", icon: FileText, fields: [
    AREA("query_template", 'resource.type="cloud_run_revision" AND textPayload:"{tenant_id}"',
                           "Advanced filter · {identifier} placeholders"),
    TEXT("limit",          "100", "Max entries"),
  ]},
  { key: "azure_monitor", category: "logs", label: "Azure Monitor (KQL)", icon: FileText, fields: [
    AREA("query_template",
         'AppTraces | where Message contains "{tenant_id}" | order by TimeGenerated desc | take 100',
         "KQL query"),
  ]},
  { key: "sentry", category: "logs", label: "Sentry events", icon: FileText, fields: [
    AREA("query_template", "user.id:{user_id} is:unresolved", "Sentry search query"),
    TEXT("stats_period",   "24h", "Time window (e.g. 24h, 7d)"),
    TEXT("limit",          "50", "Max events"),
  ]},

  // ─── Search ──────────────────────────────────────────────────────────────
  { key: "elasticsearch", category: "search", label: "Elasticsearch query", icon: Layers, fields: [
    TEXT("index",          "logs-*", "Index pattern"),
    AREA("query_template", '{"match": {"message": "{tenant_id}"}}', "DSL JSON or query_string · {identifier} placeholders"),
    TEXT("size",           "50", "Max hits"),
  ]},
  { key: "opensearch", category: "search", label: "OpenSearch query", icon: Layers, fields: [
    TEXT("index",          "logs-*", "Index pattern"),
    AREA("query_template", '{"match": {"message": "{tenant_id}"}}', "DSL JSON or query_string · {identifier} placeholders"),
    TEXT("size",           "50", "Max hits"),
  ]},
];

const CATEGORIES = [
  { key: "db",     label: "Databases" },
  { key: "logs",   label: "Logs" },
  { key: "search", label: "Search" },
];

const newId = () => Math.random().toString(36).slice(2, 10);

export function ProblemTypeEditor({ value, onChange, onDelete, lang = "pt", squadId = null }) {
  const [stepPickerOpen, setStepPickerOpen] = useState(false);
  const [ragSuggestions, setRagSuggestions] = useState(null);
  const [kbSuggestions, setKbSuggestions] = useState(null);
  const [loadingRag, setLoadingRag] = useState(false);
  const [loadingKb, setLoadingKb] = useState(false);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [autoFilledKeys, setAutoFilledKeys] = useState(new Set()); // identifier keys auto-filled from RAG
  const [autoFilledSteps, setAutoFilledSteps] = useState(new Set()); // step ids auto-filled
  const T = (pt, en) => (lang === "pt" ? pt : en);

  const identifiers = value.identifiers || [];
  const flow_steps = value.flow_steps || [];

  const set = (patch) => onChange({ ...value, ...patch });
  const updateIdent = (i, patch) => {
    // editing an auto-filled ident → strip the chip
    const old = identifiers[i] || {};
    if (autoFilledKeys.has(old.key) && (patch.label != null || patch.regex != null)) {
      const next = new Set(autoFilledKeys); next.delete(old.key); setAutoFilledKeys(next);
    }
    set({ identifiers: identifiers.map((x, j) => j === i ? { ...x, ...patch } : x) });
  };
  const removeIdent = (i) => set({ identifiers: identifiers.filter((_, j) => j !== i) });
  const addIdent = () => set({ identifiers: [...identifiers, { key: "new_id", label: "New identifier", regex: "" }] });

  const updateStep = (i, patch) => {
    const old = flow_steps[i] || {};
    if (autoFilledSteps.has(old.id) && (patch.label != null || patch.config != null)) {
      const next = new Set(autoFilledSteps); next.delete(old.id); setAutoFilledSteps(next);
    }
    set({ flow_steps: flow_steps.map((x, j) => j === i ? { ...x, ...patch } : x) });
  };
  const updateStepConfig = (i, field, val) => updateStep(i, { config: { ...(flow_steps[i].config || {}), [field]: val } });
  const removeStep = (i) => set({ flow_steps: flow_steps.filter((_, j) => j !== i) });
  const addStep = (typeKey) => {
    const def = STEP_TYPES.find(s => s.key === typeKey);
    set({ flow_steps: [...flow_steps, { id: newId(), step_type: typeKey, label: def.label, config: {} }] });
    setStepPickerOpen(false);
  };

  // ─── RAG / KB suggestion endpoints ───────────────────────────────────────
  const loadRagIdentifierSuggestions = async () => {
    if (!squadId) return;
    setLoadingRag(true);
    try {
      const r = await api.get(`/squads/${squadId}/rag-suggestions/identifiers`);
      setRagSuggestions(r.data.suggestions || []);
    } catch (e) {
      setRagSuggestions([]);
    } finally {
      setLoadingRag(false);
    }
  };
  const loadKbProblemTypes = async () => {
    if (!squadId) return;
    setLoadingKb(true);
    try {
      const r = await api.get(`/squads/${squadId}/kb-suggestions/problem-types`);
      setKbSuggestions(r.data.problem_types || []);
    } catch (e) {
      setKbSuggestions([]);
    } finally {
      setLoadingKb(false);
    }
  };
  const applyIdentifierFromRag = (sugg) => {
    if (identifiers.some(x => x.key === sugg.key)) return;
    set({
      identifiers: [...identifiers, {
        key: sugg.key,
        label: sugg.example_template?.slice(0, 60) || sugg.key,
        regex: "",
      }],
    });
    setAutoFilledKeys(new Set([...autoFilledKeys, sugg.key]));
  };
  const autoFillFlowFromRag = async () => {
    if (!squadId || !value.key) return;
    setLoadingFlow(true);
    try {
      const idsFlat = {};
      for (const id of identifiers) if (id.key) idsFlat[id.key] = "";
      const r = await api.post(`/squads/${squadId}/rag-flow-suggestions`, {
        problem_type: value.key,
        identifiers: idsFlat,
      });
      const steps = r.data.flow_steps || [];
      if (!steps.length) {
        return;
      }
      const newIds = new Set(autoFilledSteps);
      const stamped = steps.map(s => {
        const id = newId();
        newIds.add(id);
        return { ...s, id };
      });
      set({ flow_steps: [...flow_steps, ...stamped] });
      setAutoFilledSteps(newIds);
    } catch (e) {
      // silent — operator can retry
    } finally {
      setLoadingFlow(false);
    }
  };
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= flow_steps.length) return;
    const next = [...flow_steps];
    [next[i], next[j]] = [next[j], next[i]];
    set({ flow_steps: next });
  };

  const renderField = (i, step, field) => {
    const v = step.config?.[field.name] ?? "";
    const tid = `pt-step-${i}-${field.name}`;
    const placeholder = field.hint ? `${field.name} · ${field.hint}` : `${field.name} · ${field.placeholder || ""}`;
    if (field.options) {
      return (
        <select key={field.name} value={v} onChange={(e) => updateStepConfig(i, field.name, e.target.value)}
          className="w-full bg-black/60 border border-white/10 px-2 py-1.5 text-[11px] font-mono" data-testid={tid}>
          <option value="">— {field.name} —</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (field.textarea) {
      return (
        <textarea key={field.name} value={v} onChange={(e) => updateStepConfig(i, field.name, e.target.value)}
          placeholder={placeholder} rows={field.list ? 1 : 2}
          className="w-full bg-black/60 border border-white/10 px-2 py-1.5 text-[11px] font-mono" data-testid={tid} />
      );
    }
    return (
      <input key={field.name} value={v} onChange={(e) => updateStepConfig(i, field.name, e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black/60 border border-white/10 px-2 py-1.5 text-[11px] font-mono" data-testid={tid} />
    );
  };

  return (
    <div className="bg-white/5 p-3 border border-white/10 space-y-3" data-testid={`pt-editor-${value.key}`}>
      {/* Header — key (datalist with KB suggestions) + label + toggles + delete */}
      <div className="flex gap-2 items-center flex-wrap">
        <input
          value={value.key}
          onChange={(e) => set({ key: e.target.value })}
          onFocus={() => { if (kbSuggestions === null && squadId) loadKbProblemTypes(); }}
          placeholder="key"
          list={`pt-kb-keys-${value.key || "new"}`}
          className="bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono w-40"
          data-testid="pt-key"
        />
        {kbSuggestions && kbSuggestions.length > 0 && (
          <datalist id={`pt-kb-keys-${value.key || "new"}`}>
            {kbSuggestions.map((s) => <option key={s.key} value={s.key}>{`${s.key} (${s.support_total}x)`}</option>)}
          </datalist>
        )}
        <input value={value.label} onChange={(e) => set({ label: e.target.value })} placeholder="Label"
          className="bg-black/40 border border-white/10 px-2 py-1 text-xs flex-1 min-w-[120px]" data-testid="pt-label" />
        <label className="flex items-center gap-1 text-[10px] text-white/60 font-mono cursor-pointer">
          <input type="checkbox" checked={value.prefer_rag !== false}
            onChange={(e) => set({ prefer_rag: e.target.checked })}
            data-testid="pt-prefer-rag" />
          {T("prefer RAG", "prefer RAG")}
        </label>
        <label className="flex items-center gap-1 text-[10px] text-white/60 font-mono cursor-pointer">
          <input type="checkbox" checked={value.auto_execute_flow !== false}
            onChange={(e) => set({ auto_execute_flow: e.target.checked })}
            data-testid="pt-auto-exec" />
          {T("auto-execute", "auto-execute")}
        </label>
        <button type="button" onClick={onDelete} className="text-red-400 px-2" data-testid="pt-delete"><X className="w-3.5 h-3.5" /></button>
      </div>

      {squadId && (
        <div className="text-[10px] text-white/40 font-mono bg-primary/5 border border-primary/20 px-2 py-1.5">
          {T(
            "Sugestões vêm de: vars do código (RAG) + tipos de problema observados (KB). Identifiers manuais são OVERRIDES — quando RAG está disponível, o sistema infere automaticamente.",
            "Suggestions come from: code vars (RAG) + observed problem types (KB). Manual identifiers are OVERRIDES — when RAG is available, the system infers automatically."
          )}
        </div>
      )}

      {/* Identifiers */}
      <div>
        <div className="overline mb-2 flex justify-between items-center">
          <span>{T("Identifiers (regex)", "Identifiers (regex)")} · {identifiers.length}</span>
          <div className="flex gap-2 items-center">
            {squadId && (
              <button type="button" onClick={loadRagIdentifierSuggestions}
                className="text-[10px] text-primary hover:underline flex items-center gap-1"
                data-testid="pt-ident-suggest-rag">
                <Sparkles className="w-3 h-3" /> {loadingRag ? T("...", "...") : T("Suggest from RAG", "Suggest from RAG")}
              </button>
            )}
            <button type="button" onClick={addIdent} className="text-[10px] text-primary hover:underline" data-testid="pt-ident-add">+ {T("identifier", "identifier")}</button>
          </div>
        </div>

        {ragSuggestions !== null && (
          <div className="mb-2 bg-black/40 border border-primary/20 px-2 py-1.5 max-h-[120px] overflow-y-auto">
            {ragSuggestions.length === 0 ? (
              <div className="text-[10px] text-white/40 font-mono">{T("Sem sugestões (squad sem RAG indexado).", "No suggestions (squad not RAG-indexed).")}</div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {ragSuggestions.map((s) => {
                  const already = identifiers.some(x => x.key === s.key);
                  return (
                    <button key={`${s.source}:${s.key}`} type="button" onClick={() => applyIdentifierFromRag(s)}
                      disabled={already}
                      title={s.example_template}
                      className={`text-[10px] font-mono px-1.5 py-0.5 border ${already ? "border-white/10 text-white/30 line-through" : "border-primary/40 text-primary hover:bg-primary/10"}`}>
                      [{s.source}] {s.key}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          {identifiers.map((id, i) => (
            <div key={i} className="flex gap-1.5 items-center" data-testid={`pt-ident-${i}`}>
              <input value={id.key || ""} onChange={(e) => updateIdent(i, { key: e.target.value })} placeholder="key"
                className="bg-black/60 border border-white/10 px-2 py-1 text-[11px] font-mono w-32" />
              <input value={id.label || ""} onChange={(e) => updateIdent(i, { label: e.target.value })} placeholder="Label"
                className="bg-black/60 border border-white/10 px-2 py-1 text-[11px] flex-1" />
              <input value={id.regex || ""} onChange={(e) => updateIdent(i, { regex: e.target.value })} placeholder="regex pattern"
                className="bg-black/60 border border-white/10 px-2 py-1 text-[11px] font-mono flex-1" />
              {autoFilledKeys.has(id.key) && (
                <span className="text-[9px] font-mono px-1 py-0.5 bg-white/10 text-white/60" title={T("Sugestão do RAG — edite pra remover o marcador", "RAG-suggested — edit to clear chip")}>
                  auto
                </span>
              )}
              <button type="button" onClick={() => removeIdent(i)} className="text-red-400 px-1.5"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {identifiers.length === 0 && <div className="text-[10px] text-white/30 font-mono">{T("Sem identifiers", "No identifiers")}</div>}
        </div>
      </div>

      {/* Structured Flow Steps */}
      <div>
        <div className="overline mb-2 flex justify-between items-center">
          <span>{T("Flow steps", "Flow steps")} · {flow_steps.length}</span>
          <div className="flex gap-2 items-center">
            {squadId && (
              <button type="button" onClick={autoFillFlowFromRag} disabled={loadingFlow || !value.key}
                className="text-[10px] text-primary hover:underline flex items-center gap-1 disabled:opacity-40"
                data-testid="pt-flow-autofill-rag">
                <Wand2 className="w-3 h-3" /> {loadingFlow ? T("gerando...", "generating...") : T("Auto-fill from RAG", "Auto-fill from RAG")}
              </button>
            )}
            <div className="relative">
              <button type="button" onClick={() => setStepPickerOpen(!stepPickerOpen)} className="text-[10px] text-primary hover:underline" data-testid="pt-step-add">+ {T("step", "step")}</button>
              {stepPickerOpen && (
                <div className="absolute right-0 top-5 z-10 bg-black border border-white/20 shadow-xl min-w-[280px] max-h-[420px] overflow-y-auto">
                  {CATEGORIES.map((cat) => (
                    <div key={cat.key}>
                      <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-white/40 bg-white/5 border-b border-white/10">{cat.label}</div>
                      {STEP_TYPES.filter((s) => s.category === cat.key).map((s) => (
                        <button key={s.key} type="button" onClick={() => addStep(s.key)}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 flex items-center gap-2" data-testid={`pt-step-add-${s.key}`}>
                          <s.icon className="w-3 h-3 text-primary shrink-0" /> {s.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {flow_steps.map((step, i) => {
            const def = STEP_TYPES.find(s => s.key === step.step_type) || STEP_TYPES[0];
            return (
              <div key={step.id || i} className="bg-black/30 border border-white/10 p-2.5" data-testid={`pt-step-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <def.icon className="w-3 h-3 text-primary shrink-0" />
                  <span className="text-[11px] font-mono text-primary uppercase">{step.step_type}</span>
                  <input value={step.label || ""} onChange={(e) => updateStep(i, { label: e.target.value })} placeholder="Step label"
                    className="bg-black/60 border border-white/10 px-2 py-1 text-[11px] flex-1" data-testid={`pt-step-label-${i}`} />
                  {autoFilledSteps.has(step.id) && (
                    <span className="text-[9px] font-mono px-1 py-0.5 bg-white/10 text-white/60" title={T("Sugestão do RAG", "RAG-suggested")}>
                      auto
                    </span>
                  )}
                  <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} className="text-white/40 disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => moveStep(i, +1)} disabled={i === flow_steps.length - 1} className="text-white/40 disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => removeStep(i)} className="text-red-400"><X className="w-3.5 h-3.5" /></button>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {def.fields.map((field) => renderField(i, step, field))}
                </div>
              </div>
            );
          })}
          {flow_steps.length === 0 && <div className="text-[10px] text-white/30 font-mono">{T("Sem flow steps", "No flow steps")}</div>}
        </div>
      </div>
    </div>
  );
}
